import { 
  Case, 
  CreateCaseRequest, 
  UpdateCaseRequest, 
  CasePageResponse,
  CaseStatus,
  CasePriority
} from '../types';
import { httpClient } from './httpClient';

// Real Case Management API implementation
export const caseApiReal = {
  // Get all cases with pagination and filtering
  getCases: async (params?: { 
    status?: CaseStatus; 
    search?: string; 
    page?: number;
    size?: number;
  }): Promise<CasePageResponse> => {
    const queryParams: Record<string, any> = {};
    
    if (params?.status) queryParams.status = params.status;
    if (params?.search) queryParams.search = params.search;
    if (params?.page !== undefined) queryParams.page = params.page;
    if (params?.size !== undefined) queryParams.size = params.size;

    return httpClient.get<CasePageResponse>('/cases', queryParams);
  },

  // Get case details by ID
  getCase: async (caseId: string): Promise<Case> => {
    return httpClient.get<Case>(`/cases/${caseId}`);
  },

  // Create new case
  createCase: async (caseData: CreateCaseRequest): Promise<Case> => {
    return httpClient.post<Case>('/cases', caseData);
  },

  // Update existing case
  updateCase: async (caseId: string, updates: UpdateCaseRequest): Promise<Case> => {
    return httpClient.put<Case>(`/cases/${caseId}`, updates);
  },

  // Delete case
  deleteCase: async (caseId: string): Promise<void> => {
    return httpClient.delete<void>(`/cases/${caseId}`);
  },
};

// Helper functions for frontend compatibility
export const caseApiHelpers = {
  // Convert backend Case to legacy format for existing components
  toLegacyCase: (backendCase: Case) => {
    // Compute a more accurate status based on case data
    let computedStatus = backendCase.status;
    
    // If backend says CREATED but we have documents, upgrade the status
    if (backendCase.status === 'CREATED' && backendCase.documentCount > 0) {
      computedStatus = 'READY'; // Documents uploaded and likely processed
    }
    
    return {
      caseId: backendCase.id,
      name: backendCase.name,
      patientName: backendCase.patientName,
      patientIdentifier: backendCase.patientIdentifier,
      description: backendCase.description,
      caseTypes: backendCase.caseTypes,
      incidentDateStart: backendCase.incidentDateStart,
      incidentDateEnd: backendCase.incidentDateEnd,
      processingGuidance: backendCase.processingGuidance,
      instructionLetterData: backendCase.instructionLetterData
        ? (typeof backendCase.instructionLetterData === 'string'
          ? JSON.parse(backendCase.instructionLetterData)
          : backendCase.instructionLetterData)
        : undefined,
      instructionLetterDocumentId: backendCase.instructionLetterDocumentId,
      createdDate: backendCase.createdAt,
      lastModified: backendCase.updatedAt,
      status: computedStatus,
      documentCount: backendCase.documentCount,
      processingProgress: [
        { stage: 'Files uploaded', status: backendCase.documentCount > 0 ? 'completed' as const : 'pending' as const },
        { stage: 'OCR complete', status: backendCase.documentCount > 0 ? 'completed' as const : 'pending' as const },
        { stage: 'Documents classified', status: backendCase.documentCount > 0 ? 'completed' as const : 'pending' as const },
        { stage: 'Chronology built', status: backendCase.documentCount > 0 ? 'completed' as const : 'pending' as const },
        { stage: 'Ready for review', status: backendCase.documentCount > 0 ? 'completed' as const : 'pending' as const }
      ]
    };
  },

  // Map legacy status to backend status
  mapLegacyStatus: (legacyStatus: string): CaseStatus => {
    const statusMap: Record<string, CaseStatus> = {
      'uploading': 'UPLOADING',
      'processing': 'PROCESSING', 
      'ready': 'READY',
      'error': 'ERROR'
    };
    return statusMap[legacyStatus] || 'CREATED';
  },

  // Map backend status to legacy status for compatibility
  mapToLegacyStatus: (backendStatus: CaseStatus): string => {
    const statusMap: Record<CaseStatus, string> = {
      'CREATED': 'uploading',
      'UPLOADING': 'uploading',
      'QUEUED': 'uploading',
      'PROCESSING': 'processing',
      'READY': 'ready',
      'REVIEW': 'ready',
      'COMPLETE': 'ready',
      'ERROR': 'error'
    };
    return statusMap[backendStatus] || 'error';
  }
};